import {
  serviceTemplate,
  yearTemplate,
  madeInTemplate,
  finalRestingTemplate,
  memorialContributionsTemplate,
} from 'utils/tinyMCE/templates'
import { userTypes, formFieldKeys } from '@messenger/consts'
import { insertTemplate } from './insertTemplate'
import { caseDetailSpan } from 'utils/tinyMCE/caseDetails'
import { replaceInitialValuesAndDates } from 'utils/tinyMCE/replaceInitialValues'
import i18n from '../../../i18n'

// Genealogy fields per side, in the same generational order the Edit Case
// modal shows them (grandmother → her parents, grandfather → his parents).
// labelKey resolves the individual submenu item's i18n label.
const GENEALOGY_SIDE_FIELDS = {
  mothersside: [
    { key: formFieldKeys.maternalGrandmother, labelKey: 'grandmother' },
    {
      key: formFieldKeys.maternalGrandmothersMother,
      labelKey: 'greatgrandmother',
      indent: true,
    },
    {
      key: formFieldKeys.maternalGrandmothersFather,
      labelKey: 'greatgrandfather',
      indent: true,
    },
    { key: formFieldKeys.maternalGrandfather, labelKey: 'grandfather' },
    {
      key: formFieldKeys.maternalGrandfathersMother,
      labelKey: 'greatgrandmother',
      indent: true,
    },
    {
      key: formFieldKeys.maternalGrandfathersFather,
      labelKey: 'greatgrandfather',
      indent: true,
    },
  ],
  fathersside: [
    { key: formFieldKeys.paternalGrandmother, labelKey: 'grandmother' },
    {
      key: formFieldKeys.paternalGrandmothersMother,
      labelKey: 'greatgrandmother',
      indent: true,
    },
    {
      key: formFieldKeys.paternalGrandmothersFather,
      labelKey: 'greatgrandfather',
      indent: true,
    },
    { key: formFieldKeys.paternalGrandfather, labelKey: 'grandfather' },
    {
      key: formFieldKeys.paternalGrandfathersMother,
      labelKey: 'greatgrandmother',
      indent: true,
    },
    {
      key: formFieldKeys.paternalGrandfathersFather,
      labelKey: 'greatgrandfather',
      indent: true,
    },
  ],
}

// TinyMCE menu labels are plain text, so indent great-grandparents with
// leading non-breaking spaces (regular spaces collapse) for a small hierarchy.
const MENU_INDENT = '\u00A0'.repeat(4)

const hasFieldValue = (value) =>
  typeof value === 'string' && value.trim().length > 0

export const insertableTemplates = (editorProps) => {
  const templates = []

  const insertableProps = { ...editorProps, templates }

  const handlers = [
    handleFinalResting,
    handleDefaultService,
    handleDefaultVisitation,
    handleServices,
    handleGenealogy,
    handleBirthYear,
    handleDeathYear,
    handleMadeIn,
    handleMemorialContributions,
  ]

  handlers.forEach((handler) => handler(insertableProps))

  return templates
}

export default insertableTemplates

const createYearHandler = (fieldName, menuText) => (insertableProps) => {
  const { editor, caseDetails, templates } = insertableProps
  const date = caseDetails[fieldName]
  const isDate = date instanceof Date
  if (!date || !isDate) return

  const templateProps = {
    fieldName,
  }

  const yearHtml = yearTemplate({
    editorProps: insertableProps,
    templateProps,
    firstInsertion: true,
  })

  templates.push({
    type: 'menuitem',
    text: menuText,
    onAction: () => insertTemplate(editor, yearHtml),
  })
}

/**
 * Factory function that creates handlers for default service/visitation insert menu items.
 * Follows the same pattern as createYearHandler.
 */
const createDefaultServiceHandler = ({
  type,
  caseDetailKey,
  dataTemplateAttribute,
  translationKey,
}) => {
  return (insertableProps) => {
    const { editor, caseDetails, templates, user } = insertableProps
    const allServices = caseDetails.services || []

    // Filter to only items of this type
    const itemsOfType = allServices.filter(
      (s) => s.type?.toLowerCase() === type,
    )

    const isAdminUser =
      user?.type === userTypes.superAdmin ||
      user?.type === userTypes.corporationAdmin ||
      false
    const canUserInsert = user?.actAsAdmin || isAdminUser || false
    if (itemsOfType.length === 0 && !canUserInsert) return

    let defaultItem = caseDetails?.[caseDetailKey]
    let itemIndex = -1

    if (defaultItem) {
      itemIndex = itemsOfType.findIndex(
        (s) =>
          s.id === defaultItem.id ||
          (s.location === defaultItem.location && s.date === defaultItem.date),
      )
    } else {
      itemIndex = itemsOfType.findIndex((s) => s.default === true)
      if (itemIndex !== -1) {
        defaultItem = itemsOfType[itemIndex]
      }
    }

    const hasDefault = itemIndex !== -1 && defaultItem && defaultItem?.id
    if (!hasDefault) return

    const templateProps = { serviceId: defaultItem.id }

    const templateHtml = serviceTemplate({
      editorProps: insertableProps,
      templateProps,
      firstInsertion: true,
      dataTemplateAttribute,
    })

    templates.push({
      type: 'menuitem',
      text: i18n.t(`editor.tinyMCE.insertDetail.${translationKey}`),
      onAction: () => insertTemplate(editor, templateHtml),
    })
  }
}

const handleDefaultService = createDefaultServiceHandler({
  type: 'service',
  caseDetailKey: 'default_service',
  dataTemplateAttribute: 'service',
  translationKey: 'defaultservice',
})

const handleDefaultVisitation = createDefaultServiceHandler({
  type: 'visitation',
  caseDetailKey: 'default_visitation',
  dataTemplateAttribute: 'visitation',
  translationKey: 'defaultvisitation',
})

const handleServices = (insertableProps) => {
  const { editor, caseDetails, templates } = insertableProps
  const services = caseDetails.services || []
  if (services.length === 0) return

  const specificServicesMenu = {
    type: 'nestedmenuitem',
    text: i18n.t('editor.tinyMCE.insertDetail.services'),
    getSubmenuItems: () => {
      const items = []

      services.forEach((service, i) => {
        const hasService = service?.id
        if (!hasService) {
          console.error(
            `Service at index ${i} is missing or does not have an ID`,
          )
          return
        }

        const templateProps = { serviceId: service.id }

        const location = service.location
        const type =
          (service?.type || '').charAt(0).toUpperCase() +
            (service?.type || '').slice(1) || 'Service'
        const serviceName = location
          ? `${i18n.t(`editor.tinyMCE.insertDetail.${type.toLowerCase()}`)}: ${location}`
          : `${i18n.t(`editor.tinyMCE.insertDetail.${type.toLowerCase()}`)} ${i + 1}`

        const serviceHtml = serviceTemplate({
          editorProps: insertableProps,
          templateProps,
          firstInsertion: true,
          dataTemplateAttribute: 'specific_service',
        })

        items.push({
          type: 'menuitem',
          text: serviceName,
          onAction: () => insertTemplate(editor, serviceHtml),
        })
      })

      return items
    },
  }

  templates.push(specificServicesMenu)
}

const handleGenealogy = (insertableProps) => {
  const {
    editor,
    caseDetails,
    templates,
    selectedTheme,
    dateFormat,
    pageSetLocale,
  } = insertableProps

  const insertKeys = (keys) => {
    const content = replaceInitialValuesAndDates(
      keys
        .map((key) => `<p>${caseDetailSpan(key, selectedTheme, editor)}</p>`)
        .join(''),
      caseDetails,
      dateFormat,
      pageSetLocale,
    )
    insertTemplate(editor, content)
  }

  // Each side is its own submenu: "Insert all" followed by an individual item
  // per populated field. Sides/fields with no data are omitted.
  const buildSideMenu = (sideKey) => {
    const presentFields = GENEALOGY_SIDE_FIELDS[sideKey].filter(({ key }) =>
      hasFieldValue(caseDetails[key]),
    )
    if (presentFields.length === 0) return null

    return {
      type: 'nestedmenuitem',
      text: i18n.t(`editor.tinyMCE.insertDetail.${sideKey}`),
      getSubmenuItems: () => [
        {
          type: 'menuitem',
          text: i18n.t('editor.tinyMCE.insertDetail.insertall'),
          onAction: () => insertKeys(presentFields.map(({ key }) => key)),
        },
        ...presentFields.map(({ key, labelKey, indent }) => ({
          type: 'menuitem',
          text:
            (indent ? MENU_INDENT : '') +
            i18n.t(`editor.tinyMCE.insertDetail.${labelKey}`),
          onAction: () => insertKeys([key]),
        })),
      ],
    }
  }

  const sideMenus = ['mothersside', 'fathersside']
    .map(buildSideMenu)
    .filter(Boolean)

  // No genealogy data on the case → don't surface the menu at all.
  if (sideMenus.length === 0) return

  templates.push({
    type: 'nestedmenuitem',
    text: i18n.t('editor.tinyMCE.insertDetail.genealogy'),
    getSubmenuItems: () => sideMenus,
  })
}

const handleBirthYear = createYearHandler(
  'date_of_birth',
  i18n.t('editor.tinyMCE.insertDetail.yearofbirth'),
)
const handleDeathYear = createYearHandler(
  'date_of_death',
  i18n.t('editor.tinyMCE.insertDetail.yearofdeath'),
)

const handleMadeIn = (insertableProps) => {
  const { editor, templates } = insertableProps

  const madeInHtml = madeInTemplate({
    editorProps: insertableProps,
    firstInsertion: true,
  })

  templates.push({
    type: 'menuitem',
    text: i18n.t('editor.tinyMCE.insertDetail.printedin'),
    onAction: () => insertTemplate(editor, madeInHtml),
  })
}

const handleFinalResting = (insertableProps) => {
  const { editor, caseDetails, templates } = insertableProps

  const {
    interment_place,
    interment_address,
    interment_city,
    interment_state,
    interment_postal_code,
  } = caseDetails

  const hasInterment =
    interment_place ||
    interment_address ||
    interment_city ||
    interment_state ||
    interment_postal_code

  if (!hasInterment) return

  const finalRestingHtml = finalRestingTemplate({
    editorProps: insertableProps,
    firstInsertion: true,
  })

  templates.push({
    type: 'menuitem',
    text: i18n.t('editor.tinyMCE.insertDetail.finalresting'),
    onAction: () =>
      editor.execCommand('mceInsertTemplate', false, finalRestingHtml),
  })
}

const handleMemorialContributions = (insertableProps) => {
  const { editor, templates, caseDetails } = insertableProps

  const memorialContributions = caseDetails?.memorial_contributions
  const hasValid =
    Array.isArray(memorialContributions) &&
    memorialContributions.some(
      (memorials) => memorials?.name && memorials.name.trim().length > 0,
    )

  if (!hasValid) return

  const memorialContributionsHtml = memorialContributionsTemplate({
    editorProps: insertableProps,
    firstInsertion: true,
  })

  templates.push({
    type: 'menuitem',
    text: i18n.t('editor.tinyMCE.insertDetail.memorialcontributions'),
    onAction: () => insertTemplate(editor, memorialContributionsHtml),
  })
}
